import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Loader2, Download, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { convertToCSV, downloadCSV } from '@/utils/csvExport';

const Outstandings = () => {
  const { API } = useAuth();
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ total_outstanding: 0, by_status: [], total_drivers: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [recordsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/outstandings`),
        axios.get(`${API}/outstandings/summary`),
      ]);
      setRecords(recordsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch outstandings');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = () => {
    if (records.length === 0) { toast.error('No data'); return; }
    const headers = [
      { label: 'Driver Name', key: 'driver_name' }, { label: 'Driver ID', key: 'driver_id_ext' },
      { label: 'Vehicle', key: 'vehicle_number' }, { label: 'Current Vehicle', key: 'current_vehicle' },
      { label: 'Opening OS', key: 'opening_os' }, { label: 'Hisaab Amount', key: 'hisaab_amount' },
      { label: 'Received', key: 'received' }, { label: 'Paid', key: 'paid' },
      { label: 'Closing OS', key: 'closing_os' }, { label: 'Status', key: 'status' },
    ];
    const csv = convertToCSV(records, headers);
    downloadCSV(csv, `outstandings_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      overdue: 'bg-red-50 text-red-700 border border-red-200',
      pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    };
    const icons = {
      paid: <CheckCircle2 className="w-3 h-3" />,
      overdue: <AlertCircle className="w-3 h-3" />,
      pending: <Clock className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100'}`}>
        {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const overdue = summary.by_status.find(s => s.status === 'overdue');
  const paid = summary.by_status.find(s => s.status === 'paid');

  return (
    <div className="space-y-6" data-testid="outstandings-section">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg"><AlertCircle className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-700" data-testid="total-os">
                  {summary.total_outstanding.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Overdue</p>
            <p className="text-2xl font-bold text-amber-700">{overdue?.count || 0} drivers</p>
            <p className="text-xs text-slate-400">{(overdue?.total_closing || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Cleared</p>
            <p className="text-2xl font-bold text-emerald-700">{paid?.count || 0} drivers</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-slate-900">Outstanding Ledger</CardTitle>
            <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-os-btn">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="os-table">
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Driver Name</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Driver ID</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Current Alloc.</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Opening OS</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hisaab</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Received</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paid</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 font-bold">Closing OS</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((r) => (
                    <TableRow key={r.id} className="hover:bg-slate-50/50 border-b border-slate-100">
                      <TableCell className="font-medium text-sm">{r.driver_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.driver_id_ext}</TableCell>
                      <TableCell className="font-mono text-xs">{r.vehicle_number}</TableCell>
                      <TableCell className="font-mono text-xs">{r.current_vehicle || '-'}</TableCell>
                      <TableCell className="text-sm">{r.opening_os?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-sm">{r.hisaab_amount?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-sm text-emerald-700">{r.received?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-sm text-red-700">{r.paid?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className={`text-sm font-bold ${r.closing_os > 0 ? 'text-red-700' : r.closing_os < 0 ? 'text-blue-700' : 'text-emerald-700'}`}>
                        {r.closing_os?.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell>{getStatusBadge(r.status)}</TableCell>
                    </TableRow>
                  ))}
                  {records.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12 text-slate-400 text-sm">
                        No outstanding records. Upload hisaab or record payments to generate.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Outstandings;
